import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft2, Cup } from "iconsax-react";

import MobileLayout from "../../components/MobileLayout";
import { Md3Button } from "../../shared/components";
import { useGamification } from "../../hooks/useGamification";
import { shuffle, type GameWord } from "../../hooks/useGameWords";
import {
  CompleteCard,
  Feedback,
  GamePageRoot,
  GameTitle,
  GameTopBar,
  MediaBox,
  OptionBtn,
  OptionsGrid,
  ProgressText,
  ScoreBadge,
} from "./gameStyles";

type Props = {
  words: GameWord[];
  onExit?: () => void;
};

type Question = {
  word: GameWord;
  options: string[];
};

function buildQuestions(words: GameWord[]): Question[] {
  const pool = words.filter((w) => w.palavra?.trim());
  return pool.map((word) => {
    const distractors = shuffle(
      pool.filter((w) => w.uid !== word.uid).map((w) => w.palavra)
    ).slice(0, 3);
    const options = shuffle([word.palavra, ...distractors]);
    return { word, options };
  });
}

const CompleteSignGame: React.FC<Props> = ({ words, onExit }) => {
  const navigate = useNavigate();
  const { addPoints } = useGamification();
  const questions = useMemo(() => buildQuestions(words), [words]);
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const current = questions[index];
  const answered = selected != null;
  const isCorrect =
    selected != null &&
    selected.toLowerCase() === current?.word.palavra.toLowerCase();

  const handlePick = async (option: string) => {
    if (answered || !current) return;
    setSelected(option);
    const ok = option.toLowerCase() === current.word.palavra.toLowerCase();
    if (ok) {
      setScore((s) => s + 1);
      await addPoints("GAME_MEMORY");
    }
  };

  const handleNext = () => {
    if (index + 1 >= questions.length) {
      setDone(true);
      return;
    }
    setIndex((i) => i + 1);
    setSelected(null);
  };

  if (!current && !done) {
    return (
      <MobileLayout
        headerTitle="Complete o Sinal"
        headerType="private"
        showBannerAd={false}
      >
        <GamePageRoot>
          <p>Não há palavras suficientes para jogar.</p>
          <Md3Button title="Voltar" onClick={() => navigate("/games")} />
        </GamePageRoot>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout
      headerTitle="Complete o Sinal"
      headerType="private"
      showBannerAd={false}
    >
      <GamePageRoot>
        <GameTopBar>
          <Md3Button
            title="Voltar"
            variant="text"
            onClick={() => (onExit ? onExit() : navigate("/games"))}
            icon={<ArrowLeft2 size={18} />}
          />
          <ScoreBadge>
            <Cup size={16} variant="Bold" />
            {score}
          </ScoreBadge>
        </GameTopBar>

        {done ? (
          <CompleteCard>
            <GameTitle>Parabéns!</GameTitle>
            <ProgressText>
              Você acertou {score} de {questions.length}
            </ProgressText>
            <Md3Button
              title="Jogar de novo"
              onClick={() => window.location.reload()}
            />
            <Md3Button
              title="Voltar aos jogos"
              variant="tonal"
              onClick={() => navigate("/games")}
            />
          </CompleteCard>
        ) : (
          <>
            <ProgressText>
              Pergunta {index + 1} de {questions.length}
            </ProgressText>
            <MediaBox>
              {(() => {
                if (current.word.video) {
                  return (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video
                      src={current.word.video}
                      controls
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  );
                }
                if (current.word.image) {
                  return (
                    <img src={current.word.image} alt={current.word.palavra} />
                  );
                }
                return <span>?</span>;
              })()}
            </MediaBox>
            <OptionsGrid>
              {current.options.map((option) => {
                let state: "idle" | "correct" | "wrong" | "disabled" = "idle";
                if (answered) {
                  if (
                    option.toLowerCase() === current.word.palavra.toLowerCase()
                  ) {
                    state = "correct";
                  } else if (option === selected) {
                    state = "wrong";
                  } else {
                    state = "disabled";
                  }
                }
                return (
                  <OptionBtn
                    key={option}
                    type="button"
                    $state={state}
                    disabled={answered}
                    onClick={() => handlePick(option)}
                  >
                    {option}
                  </OptionBtn>
                );
              })}
            </OptionsGrid>
            {answered ? (
              <>
                <Feedback $ok={isCorrect}>
                  {isCorrect ? "Correto!" : `Resposta: ${current.word.palavra}`}
                </Feedback>
                <Md3Button title="Próxima" onClick={handleNext} />
              </>
            ) : null}
          </>
        )}
      </GamePageRoot>
    </MobileLayout>
  );
};

export default CompleteSignGame;
